/**
 * @author: chenlv
 * @date: 2025/01/30
 * @desc: Tooltip 组件演示文件，展示各图表类型 Tooltip 的使用方式
 */
import React from 'react';
import {
  BarChartTooltip,
  LineChartTooltip,
  PieChartTooltip,
  MapChartTooltip,
} from './index';

// 模拟数据
const barData = [
  {
    color: '#5470C6',
    name: 'Genshin Impact',
    value: '1,234,567',
  },
  {
    color: '#91CC75',
    name: 'Honkai: Star Rail',
    value: '987,654',
  },
  {
    color: '#FAC858',
    name: 'Mobile Legends',
    value: '567,890',
  },
];

const lineData = [
  {
    color: '#5470C6',
    name: 'Steam',
    value: '12.3K',
    rawValue: 12300,
    ratio: { type: 'up' as const, value: '5.2%' },
  },
  {
    color: '#91CC75',
    name: 'Epic Games',
    value: '8.7K',
    rawValue: 8700,
    ratio: { type: 'down' as const, value: '2.1%' },
  },
  {
    color: '#FAC858',
    name: 'PlayStation Store',
    value: '5.6K',
    rawValue: 5600,
    ratio: { type: 'up' as const, value: '1.8%' },
  },
];

const pieDataItem = {
  color: '#5470C6',
  name: 'United States',
  value: '45,678',
  percent: '35.2%',
  ratio: { type: 'up' as const, value: '3.5%' },
};

const mapData = [
  {
    label: 'DAU',
    value: '2,345,678',
    rawValue: 2345678,
  },
  {
    label: 'Revenue',
    value: '$1,234,567',
    rawValue: 1234567,
  },
  {
    label: 'Downloads',
    value: '567,890',
    rawValue: 567890,
  },
];

/**
 * Demo 案例配置
 * 每个配置项对应文档页面中的一个演示场景
 */
export const demoConfig = [
  {
    title: '柱状图 Tooltip（基础用法）',
    description: '展示柱状图的 Tooltip，包含时间范围和指标名称',
    code: `<BarChartTooltip
  title="Game Title"
  timeRange="2024-01-01 ~ 2024-01-07"
  metricName="Revenue"
  dataList={barData}
/>`,
    element: (
      <BarChartTooltip
        title="Game Title"
        timeRange="2024-01-01 ~ 2024-01-07"
        metricName="Revenue"
        dataList={barData}
      />
    ),
  },
  {
    title: '柱状图 Tooltip（带数据源图标）',
    description: '展示带数据源图标的柱状图 Tooltip',
    code: `<BarChartTooltip
  title="Game Title"
  timeRange="2024-01-01 ~ 2024-01-07"
  metricName="Revenue"
  dataList={barDataWithIcon}
  showDataSourceIcon={true}
/>`,
    element: (
      <BarChartTooltip
        title="Game Title"
        timeRange="2024-01-01 ~ 2024-01-07"
        metricName="Revenue"
        dataList={[
          {
            color: '#5470C6',
            name: 'Genshin Impact',
            value: '1,234,567',
            dataSourceIcon: 'https://img.alicdn.com/imgextra/i3/O1CN01Wr5Kxq1PtNqZ0L2Xs_!!6000000001897-2-tps-128-128.png',
          },
          {
            color: '#91CC75',
            name: 'Honkai: Star Rail',
            value: '987,654',
          },
        ]}
        showDataSourceIcon={true}
      />
    ),
  },
  {
    title: '折线图 Tooltip（基础用法）',
    description: '展示折线图的 Tooltip，数据按原始数值降序排列，支持环比指标',
    code: `<LineChartTooltip
  dateLabel="2024-06-15"
  metricName="Active Users"
  dataList={lineData}
/>`,
    element: (
      <LineChartTooltip
        dateLabel="2024-06-15"
        metricName="Active Users"
        dataList={lineData}
      />
    ),
  },
  {
    title: '折线图 Tooltip（带数据源图标）',
    description: '展示带数据源图标的折线图 Tooltip',
    code: `<LineChartTooltip
  dateLabel="2024-06-15"
  metricName="Active Users"
  dataList={lineData}
  dataSourceIcon="https://example.com/steam-icon.png"
  showDataSourceIcon={true}
/>`,
    element: (
      <LineChartTooltip
        dateLabel="2024-06-15"
        metricName="Active Users"
        dataList={lineData}
        dataSourceIcon="https://img.alicdn.com/imgextra/i3/O1CN01Wr5Kxq1PtNqZ0L2Xs_!!6000000001897-2-tps-128-128.png"
        showDataSourceIcon={true}
      />
    ),
  },
  {
    title: '饼状图 Tooltip（基础用法）',
    description: '展示饼状图的 Tooltip，包含百分比和环比指标',
    code: `<PieChartTooltip
  metricName="Market Share"
  timeRange="2024-Q3"
  dataItem={pieDataItem}
/>`,
    element: (
      <PieChartTooltip
        metricName="Market Share"
        timeRange="2024-Q3"
        dataItem={pieDataItem}
      />
    ),
  },
  {
    title: '饼状图 Tooltip（带数据源图标）',
    description: '展示带数据源图标的饼状图 Tooltip',
    code: `<PieChartTooltip
  metricName="Market Share"
  timeRange="2024-Q3"
  dataItem={pieDataItem}
  dataSourceIcon="https://example.com/steam-icon.png"
  showDataSourceIcon={true}
/>`,
    element: (
      <PieChartTooltip
        metricName="Market Share"
        timeRange="2024-Q3"
        dataItem={pieDataItem}
        dataSourceIcon="https://img.alicdn.com/imgextra/i3/O1CN01Wr5Kxq1PtNqZ0L2Xs_!!6000000001897-2-tps-128-128.png"
        showDataSourceIcon={true}
      />
    ),
  },
  {
    title: '地图 Tooltip（基础用法）',
    description: '展示地图的 Tooltip，包含国家名称和多个指标数据',
    code: `<MapChartTooltip
  title="United States"
  dataList={mapData}
/>`,
    element: (
      <MapChartTooltip
        title="United States"
        dataList={mapData}
      />
    ),
  },
  {
    title: '地图 Tooltip（多项指标）',
    description: '展示地图 Tooltip 中的多条指标信息',
    code: `<MapChartTooltip
  title="Japan"
  dataList={[
    { label: 'DAU', value: '1,234,567' },
    { label: 'Revenue', value: '$890,123' },
    { label: 'Downloads', value: '345,678' },
    { label: 'ARPU', value: '$0.72' },
  ]}
/>`,
    element: (
      <MapChartTooltip
        title="Japan"
        dataList={[
          { label: 'DAU', value: '1,234,567', rawValue: 1234567 },
          { label: 'Revenue', value: '$890,123', rawValue: 890123 },
          { label: 'Downloads', value: '345,678', rawValue: 345678 },
          { label: 'ARPU', value: '$0.72', rawValue: 0.72 },
        ]}
      />
    ),
  },
];

export default demoConfig;
